import { DataGrid, IconButton, Tag, Text } from '@digibee/beehive-ui';
import { Spinner, Tooltip } from '@heroui/react';
import {
  Archive,
  ClockRotateRight,
  EditPencil,
  Eye,
  Plus
} from 'iconoir-react';
import moment from 'moment';
import React from 'react';

import * as Elements from './HistoryTable.elements';

import i18n from '~/common/helpers/i18n';

const TableHistory = ({
  pipelineIds,
  pipelineById,
  loading,
  goToPipeline,
  isUpdatingVersion,
  updatePipelineVersionMinor,
  archive,
  remove,
  next,
  totalPages,
  getLatest,
  getDeployment,
  totalElements
}) => {
  const userHeader = [
    {
      id: 'version',
      accessor: 'version',
      Header: i18n.t('label.pipeline_version'),
      Cell: ({ row }) => (
        <Tag variant='version'>
          {`${i18n.t('label.pipeline_version')} ${
            row?.original?.versionMajor
          }.${row?.original?.versionMinor}`}
        </Tag>
      )
    },
    {
      id: 'deploy',
      accessor: 'deploy',
      Cell: ({ row }) => (
        <>
          {getDeployment(row.original.pipelineId)?.map?.(item => (
            <Tag
              variant='success'
              key={item.id}
              data-testid='build-pipe-history-badge'
            >
              {item.environment}
            </Tag>
          ))}
        </>
      )
    },
    {
      id: 'email',
      Header: i18n.t('label.changed_by'),
      accessor: 'email',
      Cell: ({ row }) => (
        <span>
          <Text variant='b2'>{row.original.userName}</Text>
          <Text variant='b3'>{row.original.userEmail}</Text>
        </span>
      )
    },
    {
      id: 'saveDate',
      Header: i18n.t('label.changed_at'),
      accessor: 'saveDate',
      Cell: ({ value }) => {
        const date = moment(value);
        const isLegacyHistory = date.isBefore('2000-01-01');

        return (
          <span>
            <Text variant='b3'>
              {isLegacyHistory
                ? i18n.t('label.no_data')
                : date.format('DD/MM/YYYY HH:mm')}
            </Text>
            {isLegacyHistory && (
              <Text variant='b3'>{i18n.t('label.legacy_history')}</Text>
            )}
          </span>
        );
      }
    },
    {
      id: 'pipelineDescription',
      Header: i18n.t('label.pipeline_description'),
      accessor: 'pipelineDescription',
      Cell: ({ value }) => (
        <Text css={{ _truncate: '$10' }} variant='b3' title={value}>
          {value}
        </Text>
      )
    },
    {
      id: 'action',
      Header: i18n.t('label.actions'),
      accessor: 'action',
      Cell: ({ row }) => {
        const latestRow = getLatest(row?.original?.pipelineId);

        if (row.original.disabled) {
          return (
            <Elements.WrapperButtonIcon>
              <Tooltip content={i18n.t('action.view_pipeline')}>
                <IconButton
                  variant='link'
                  emphasis='icon'
                  onClick={() => goToPipeline(row?.original?.pipelineId)}
                  data-testid='build-pipe-history-button-archive'
                >
                  <Eye />
                </IconButton>
              </Tooltip>
              <IconButton
                disabled={row?.original?.disabled}
                title={i18n.t('action.create_new_minor_version')}
                variant='link'
                emphasis='icon'
                onClick={() =>
                  updatePipelineVersionMinor({
                    id: row?.original?.pipelineId,
                    name: row?.original?.pipelineName,
                    versionMajor: row?.original?.versionMajor
                  })
                }
              >
                <Plus variant='primary' size='md' />
              </IconButton>
              <IconButton
                title={i18n.t('action.restore_pipeline')}
                variant='link'
                data-testid='build-pipe-history-button-restore'
                emphasis='icon'
                onClick={() =>
                  archive({
                    id: row?.original?.pipelineId,
                    name: row?.original?.pipelineName,
                    versionMajor: row?.original?.versionMajor
                  })
                }
              >
                <ClockRotateRight />
              </IconButton>
            </Elements.WrapperButtonIcon>
          );
        }
        return (
          <Elements.WrapperButtonIcon>
            <Tooltip
              content={
                latestRow
                  ? i18n.t('action.edit_pipeline')
                  : i18n.t('action.view_pipeline')
              }
            >
              <IconButton
                variant='link'
                emphasis='icon'
                onClick={() =>
                  latestRow
                    ? goToPipeline(row?.original?.pipelineId, row?.original)
                    : goToPipeline(row?.original?.pipelineId)
                }
                data-testid={`${
                  latestRow
                    ? 'build-pipe-history-button-edit'
                    : 'build-pipe-history-button-view'
                }`}
              >
                {latestRow ? <EditPencil /> : <Eye />}
              </IconButton>
            </Tooltip>
            <IconButton
              variant='link'
              title={i18n.t('action.create_new_minor_version')}
              disabled={isUpdatingVersion}
              onClick={() =>
                updatePipelineVersionMinor({
                  id: row?.original?.pipelineId,
                  name: row?.original?.pipelineName,
                  versionMajor: row?.original?.versionMajor
                })
              }
              data-testid='build-pipe-history-button-minor-version'
            >
              {isUpdatingVersion ? <Spinner size='sm' /> : <Plus />}
            </IconButton>
            <Tooltip content={i18n.t('action.archive_pipeline')}>
              <IconButton
                variant='link'
                emphasis='icon'
                onClick={() =>
                  remove({
                    id: row.original.pipelineId,
                    name: row.original.pipelineName,
                    versionMajor: row.original.versionMajor
                  })
                }
                data-testid='build-pipe-history-button-archive'
              >
                <Archive />
              </IconButton>
            </Tooltip>
          </Elements.WrapperButtonIcon>
        );
      }
    }
  ];
  const columns = React.useMemo(() => userHeader, [pipelineIds]);
  const data = React.useMemo(
    () => pipelineIds?.map(id => pipelineById[id]),
    [pipelineIds]
  );

  return (
    <div>
      <DataGrid
        columns={columns}
        stickyHeader
        manualPagination
        isPagination
        pageCount={totalPages}
        loading={loading}
        fetchData={tableProps => next(tableProps)}
        rowsPerPageOptions={[5, 10, 15, 20]}
        initialState={{ pageIndex: 0, pageSize: 20 }}
        data={data}
        totalElements={totalElements}
        textRowsToPage={i18n.t('label.items_per_page')}
        textNotFound={i18n.t('label.no_items_empty')}
        size='lg'
      />
    </div>
  );
};

export default TableHistory;
